import { useEffect, useState } from "react";
import SpellcheckTextarea from "@/components/SpellcheckTextarea";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { formatLicenseDateForDisplay } from "@/lib/licenseDates";
import { PageHeader } from "./layoutChrome";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { useWorkspaceMode } from "@/lib/workspaceMode";
import { useAuth } from "@/lib/auth";
import { usePlatformDesigner } from "@/lib/usePlatformDesigner";
import CommercialClassificationNotice from "@/components/commercial/CommercialClassificationNotice";
import {
  Loader2,
  Pencil,
  Plus,
  Trash2,
  RefreshCw,
} from "lucide-react";

const PAY_OPTIONS = [
  { value: "pendiente", label: "Pendiente" },
  { value: "pagado", label: "Pagado" },
  { value: "parcial", label: "Parcial" },
  { value: "cortesia", label: "Cortesía" },
  { value: "otro", label: "Otro" },
];

const EMPTY_FORM = {
  licensee_name: "",
  licensee_rfc: "",
  licensee_email: "",
  requires_invoice: false,
  datos_generales: "",
  billing_rfc: "",
  billing_razon_social: "",
  billing_email: "",
  billing_codigo_postal: "",
  billing_payment_status: "pendiente",
  billing_payment_notes: "",
  telefono_whatsapp: "",
  regimen_fiscal: "",
  uso_cfdi: "",
  portal_username: "",
  portal_password: "",
  notes: "",
  equipos_contratados: "",
};

function daysUntil(iso) {
  if (!iso) return null;
  try {
    const end = new Date(iso.slice(0, 10));
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    return Math.round((end - t) / 86400000);
  } catch {
    return null;
  }
}

function PortalAccessFields({ form, setForm, isEdit }) {
  return (
    <div className="space-y-2 border border-slate-200 rounded-sm p-3 bg-slate-50/60">
      <div className="text-[10px] font-semibold text-slate-700 uppercase tracking-wide">Acceso al sistema (comprador)</div>
      <Input
        value={form.portal_username}
        onChange={(e) => setForm((f) => ({ ...f, portal_username: e.target.value.toLowerCase().replace(/\s+/g, "") }))}
        className="h-9 text-xs font-mono"
        placeholder="Usuario de login (ej. acmex_sa)"
        autoComplete="off"
      />
      <Input
        type="password"
        value={form.portal_password}
        onChange={(e) => setForm((f) => ({ ...f, portal_password: e.target.value }))}
        className="h-9 text-xs font-mono"
        placeholder={isEdit ? "Nueva contraseña (vacío = sin cambio)" : "Contraseña inicial (mín. 6 caracteres)"}
        autoComplete="new-password"
      />
      <p className="text-[9px] text-slate-600 leading-snug">
        El comprador entra en la misma pantalla de acceso con este <strong>usuario</strong> (o puede usarse el correo técnico interno).
        Desde ahí configura empresas en el módulo Visor y los{" "}
        <strong>usuarios auxiliares del comprador</strong> en{" "}
        <strong>Panel General → Actividades de Equipo</strong> (
        <code>/?panel=equipo</code>).
      </p>
    </div>
  );
}

export default function CatalogoCompradores() {
  const { user } = useAuth();
  const isPlatformDesigner = usePlatformDesigner();
  const { mode, canSwitch } = useWorkspaceMode();
  const canUse = Boolean(user?.can_generate_licenses);
  const wrongMode = canSwitch && mode !== "distribuidor";
  const [searchParams, setSearchParams] = useSearchParams();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(() => ({ ...EMPTY_FORM }));
  const [creating, setCreating] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (searchParams.get("alta") !== "1") return;
    if (!canUse || wrongMode) {
      setSearchParams({}, { replace: true });
      return;
    }
    setCreateOpen(true);
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams, canUse, wrongMode]);

  const load = async () => {
    if (!canUse) return;
    setLoading(true);
    try {
      const { data } = await api.get("/distributor/buyers");
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error al cargar catálogo");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canUse && !wrongMode) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canUse, wrongMode]);

  const submitCreate = async () => {
    if (!createForm.licensee_name?.trim() || !createForm.licensee_rfc?.trim()) {
      toast.error("Nombre y RFC son obligatorios");
      return;
    }
    setCreating(true);
    try {
      const payload = {
        licensee_name: createForm.licensee_name.trim(),
        licensee_rfc: createForm.licensee_rfc.trim(),
        licensee_email: (createForm.licensee_email || "").trim(),
        requires_invoice: !!createForm.requires_invoice,
        datos_generales: createForm.datos_generales || "",
        billing_rfc: createForm.billing_rfc || "",
        billing_razon_social: createForm.billing_razon_social || "",
        billing_email: createForm.billing_email || "",
        billing_codigo_postal: createForm.billing_codigo_postal || "",
        billing_payment_status: createForm.billing_payment_status,
        billing_payment_notes: createForm.billing_payment_notes || "",
        telefono_whatsapp: createForm.telefono_whatsapp || "",
        regimen_fiscal: createForm.regimen_fiscal || "",
        uso_cfdi: createForm.uso_cfdi || "",
        portal_username: (createForm.portal_username || "").trim(),
        portal_password: createForm.portal_password || undefined,
        notes: createForm.notes || "",
      };
      const eq = String(createForm.equipos_contratados ?? "").trim();
      if (eq) payload.equipos_contratados = parseInt(eq, 10);
      await api.post("/distributor/buyers", payload);
      toast.success("Comprador registrado");
      setCreateOpen(false);
      setCreateForm({ ...EMPTY_FORM });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error");
    } finally {
      setCreating(false);
    }
  };

  const openEdit = (row) => {
    setEditId(row.id);
    setEditForm({
      licensee_name: row.licensee_name || "",
      licensee_rfc: row.licensee_rfc || "",
      licensee_email: row.licensee_email || "",
      requires_invoice: !!row.requires_invoice,
      datos_generales: row.datos_generales || "",
      billing_rfc: row.billing_rfc || "",
      billing_razon_social: row.billing_razon_social || "",
      billing_email: row.billing_email || "",
      billing_codigo_postal: row.billing_codigo_postal || "",
      billing_payment_status: row.billing_payment_status || "pendiente",
      billing_payment_notes: row.billing_payment_notes || "",
      telefono_whatsapp: row.telefono_whatsapp || "",
      regimen_fiscal: row.regimen_fiscal || "",
      uso_cfdi: row.uso_cfdi || "",
      portal_username: row.portal_username || "",
      portal_password: "",
      notes: row.notes || "",
      equipos_contratados:
        row.equipos_contratados != null && row.equipos_contratados !== ""
          ? String(row.equipos_contratados)
          : "",
    });
    setEditOpen(true);
  };

  const submitEdit = async () => {
    if (!editId || !editForm) return;
    setSaving(true);
    try {
      const body = {
        licensee_name: editForm.licensee_name.trim(),
        licensee_rfc: editForm.licensee_rfc.trim().toUpperCase(),
        licensee_email: (editForm.licensee_email || "").trim().toLowerCase(),
        requires_invoice: !!editForm.requires_invoice,
        datos_generales: editForm.datos_generales || "",
        billing_rfc: (editForm.billing_rfc || "").trim().toUpperCase(),
        billing_razon_social: editForm.billing_razon_social || "",
        billing_email: (editForm.billing_email || "").trim().toLowerCase(),
        billing_codigo_postal: editForm.billing_codigo_postal || "",
        billing_payment_status: editForm.billing_payment_status,
        billing_payment_notes: editForm.billing_payment_notes || "",
        telefono_whatsapp: editForm.telefono_whatsapp || "",
        regimen_fiscal: editForm.regimen_fiscal || "",
        uso_cfdi: editForm.uso_cfdi || "",
        portal_username: (editForm.portal_username || "").trim(),
        notes: editForm.notes || "",
      };
      if (editForm.portal_password && editForm.portal_password.length > 0) {
        body.portal_password = editForm.portal_password;
      }
      const eq = String(editForm.equipos_contratados ?? "").trim();
      if (eq) body.equipos_contratados = parseInt(eq, 10);
      await api.put(`/distributor/buyers/${editId}`, body);
      toast.success("Comprador actualizado");
      setEditOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error");
    } finally {
      setSaving(false);
    }
  };

  const removeBuyer = async (row) => {
    if (!window.confirm(`¿Eliminar del catálogo a ${row.licensee_name}? Solo se permite si no tiene licencias en cartera.`)) return;
    try {
      await api.delete(`/distributor/buyers/${row.id}`);
      toast.success("Eliminado");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se puede eliminar");
    }
  };

  const BillingBlock = ({ form, setForm, idPrefix }) => (
    <div className="space-y-3 border border-violet-100 bg-violet-50/40 rounded-sm p-3">
      <div className="text-[10px] font-semibold text-violet-900 uppercase">Facturación y cobro</div>
      <div>
        <Label className="text-xs">¿Requiere factura?</Label>
        <RadioGroup
          value={form.requires_invoice ? "si" : "no"}
          onValueChange={(v) => setForm((f) => ({ ...f, requires_invoice: v === "si" }))}
          className="flex gap-4 mt-2"
        >
          <div className="flex items-center gap-2">
            <RadioGroupItem value="no" id={`${idPrefix}-f0`} />
            <Label htmlFor={`${idPrefix}-f0`} className="text-xs font-normal cursor-pointer">No</Label>
          </div>
          <div className="flex items-center gap-2">
            <RadioGroupItem value="si" id={`${idPrefix}-f1`} />
            <Label htmlFor={`${idPrefix}-f1`} className="text-xs font-normal cursor-pointer">Sí</Label>
          </div>
        </RadioGroup>
      </div>
      {form.requires_invoice ? (
        <div className="grid grid-cols-1 gap-2">
          <Input
            value={form.billing_rfc}
            onChange={(e) => setForm((f) => ({ ...f, billing_rfc: e.target.value.toUpperCase() }))}
            className="h-8 text-xs font-mono"
            placeholder="RFC facturación"
          />
          <Input
            value={form.billing_razon_social}
            onChange={(e) => setForm((f) => ({ ...f, billing_razon_social: e.target.value }))}
            className="h-8 text-xs"
            placeholder="Razón social fiscal"
          />
          <div className="grid grid-cols-2 gap-2">
            <Input
              type="email"
              value={form.billing_email}
              onChange={(e) => setForm((f) => ({ ...f, billing_email: e.target.value }))}
              className="h-8 text-xs"
              placeholder="Correo fiscal"
            />
            <Input
              value={form.billing_codigo_postal}
              onChange={(e) => setForm((f) => ({ ...f, billing_codigo_postal: e.target.value }))}
              className="h-8 text-xs"
              placeholder="C.P."
            />
          </div>
          <Input
            value={form.telefono_whatsapp}
            onChange={(e) => setForm((f) => ({ ...f, telefono_whatsapp: e.target.value }))}
            className="h-8 text-xs"
            placeholder="Teléfono / WhatsApp"
          />
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] text-slate-600">Régimen fiscal (clave SAT)</Label>
              <Input
                value={form.regimen_fiscal}
                onChange={(e) => setForm((f) => ({ ...f, regimen_fiscal: e.target.value.replace(/\D/g, "").slice(0, 3) }))}
                className="h-8 text-xs font-mono mt-1"
                placeholder="601"
                maxLength={3}
              />
            </div>
            <div>
              <Label className="text-[10px] text-slate-600">Uso CFDI</Label>
              <Input
                value={form.uso_cfdi}
                onChange={(e) => setForm((f) => ({ ...f, uso_cfdi: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 3) }))}
                className="h-8 text-xs font-mono mt-1"
                placeholder="G03"
                maxLength={3}
              />
            </div>
          </div>
        </div>
      ) : null}
      <div>
        <Label className="text-[10px]">Seguimiento de cobro (comercial)</Label>
        <Select
          value={form.billing_payment_status}
          onValueChange={(v) => setForm((f) => ({ ...f, billing_payment_status: v }))}
        >
          <SelectTrigger className="h-8 text-xs mt-1"><SelectValue /></SelectTrigger>
          <SelectContent>
            {PAY_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-[10px]">Notas de cobro / facturación</Label>
        <SpellcheckTextarea
          value={form.billing_payment_notes}
          onChange={(e) => setForm((f) => ({ ...f, billing_payment_notes: e.target.value }))}
          rows={2}
          className="w-full mt-1 rounded-sm border px-2 py-1 text-[11px]"
        />
      </div>
    </div>
  );

  if (!canUse) {
    return (
      <div>
        <PageHeader title="Catálogo de compradores" subtitle="Solo disponible con permisos de distribuidor." />
        <div className="p-8 text-sm text-slate-600">Esta instalación no permite gestión de compradores de software.</div>
      </div>
    );
  }

  if (wrongMode) {
    return (
      <div>
        <PageHeader
          title="Catálogo de compradores"
          subtitle="Maestro de compradores del software (independiente de clientes fiscales del despacho)."
        />
        <div className="p-8">
          <div className="rounded-sm border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950">
            Cambia a <strong>Distribuidor</strong> en la barra lateral para usar el catálogo de compradores.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Compradores y prospectos"
        subtitle="Catálogo comercial: trials, activos, vencidos y pendientes. Distinto de clientes fiscales (Visor) y operativos (Despacho)."
        right={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" className="h-9" onClick={() => load()}>
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Actualizar
            </Button>
            <Button size="sm" className="h-9 bg-violet-700 hover:bg-violet-800" onClick={() => setCreateOpen(true)}>
              <Plus className="w-3.5 h-3.5 mr-1" /> Alta comprador
            </Button>
          </div>
        }
      />

      <div className="p-8 space-y-6">
        <CommercialClassificationNotice variant="buyer_catalog" />
        {isPlatformDesigner ? (
          <div
            className="rounded-sm border border-[#0F2D5C]/20 bg-[#0F2D5C]/[0.04] px-4 py-3 text-sm text-slate-700 flex flex-wrap items-center justify-between gap-3"
            data-testid="catalogo-disenador-hub-hint"
          >
            <span>
              Desde <strong>Compradores y Licencias</strong> puedes volver al hub comercial y usar los accesos transitorios
              de emisión, cartera y solicitudes.
            </span>
            <Link
              to="/disenador/compradores-licencias"
              className="inline-flex items-center rounded-sm bg-[#0F2D5C] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#0a2248]"
              data-testid="catalogo-link-compradores-licencias"
            >
              Volver a Compradores y Licencias
            </Link>
          </div>
        ) : null}
        <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
          {loading ? (
            <div className="p-12 flex justify-center text-slate-500">
              <Loader2 className="w-7 h-7 animate-spin" />
            </div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-600">
              No hay compradores en catálogo. Usa <strong>Alta comprador</strong> para registrar el primer comprador o
              despacho del sistema. Si necesitas operar licencias antes de la ficha única, usa los accesos transitorios de{" "}
              <Link to="/disenador/compradores-licencias" className="underline text-[#0F2D5C] font-medium">
                Compradores y Licencias
              </Link>
              .
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Código</TableHead>
                  <TableHead>RFC</TableHead>
                  <TableHead className="whitespace-nowrap">Factura</TableHead>
                  <TableHead className="whitespace-nowrap">Portal</TableHead>
                  <TableHead className="whitespace-nowrap">Cobro</TableHead>
                  <TableHead>Licencias</TableHead>
                  <TableHead className="whitespace-nowrap text-center">Equipos (meta)</TableHead>
                  <TableHead>Vence (más próximo)</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((row) => {
                  const d = daysUntil(row.license_valid_to_min);
                  let dc = "text-slate-600";
                  if (row.license_count > 0 && d != null && d >= 0 && d <= 30) dc = "text-amber-700 font-medium";
                  if (row.license_count > 0 && d != null && d < 0) dc = "text-slate-400";
                  return (
                    <TableRow key={row.id}>
                      <TableCell className="font-medium max-w-[10rem] truncate" title={row.licensee_name}>{row.licensee_name}</TableCell>
                      <TableCell className="font-mono text-[10px] text-slate-700">{row.buyer_code || "—"}</TableCell>
                      <TableCell className="font-mono text-xs">{row.licensee_rfc}</TableCell>
                      <TableCell className="text-xs">{row.requires_invoice ? "Sí" : "No"}</TableCell>
                      <TableCell className="text-xs font-mono">{row.portal_username || "—"}</TableCell>
                      <TableCell className="text-xs capitalize">{row.billing_payment_status || "—"}</TableCell>
                      <TableCell className="text-xs">{row.license_count ?? 0}</TableCell>
                      <TableCell className="text-xs text-center font-mono">
                        {row.equipos_contratados != null && row.equipos_contratados !== ""
                          ? row.equipos_contratados
                          : "—"}
                      </TableCell>
                      <TableCell className={`text-xs ${dc}`}>
                        {row.license_count && row.license_valid_to_min ? (
                          <>
                            <span className="block">{formatLicenseDateForDisplay(row.license_valid_to_min)}</span>
                            <span className="block text-[10px] font-mono text-slate-500">
                              {String(row.license_valid_to_min).slice(0, 10)}
                            </span>
                          </>
                        ) : (
                          "—"
                        )}
                        {d != null && row.license_count > 0 ? ` (${d < 0 ? `${-d} d. venc.` : `${d} d.`})` : ""}
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <Button variant="ghost" size="sm" className="h-8 px-2" asChild>
                          <Link to={`/cartera-licencias?buyer_id=${encodeURIComponent(row.id)}`}>Licencias</Link>
                        </Button>
                        <Button variant="ghost" size="sm" className="h-8 px-2" onClick={() => openEdit(row)}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-8 px-2 text-rose-600" onClick={() => removeBuyer(row)}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </div>
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto rounded-sm">
          <DialogHeader>
            <DialogTitle>{"Alta de comprador"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <Label className="text-xs">Razón social / nombre</Label>
              <Input value={createForm.licensee_name} onChange={(e) => setCreateForm((f) => ({ ...f, licensee_name: e.target.value }))} className="h-9 mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">RFC</Label>
                <Input value={createForm.licensee_rfc} onChange={(e) => setCreateForm((f) => ({ ...f, licensee_rfc: e.target.value.toUpperCase() }))} className="h-9 mt-1 font-mono" />
              </div>
              <div>
                <Label className="text-xs">Correo</Label>
                <Input value={createForm.licensee_email} onChange={(e) => setCreateForm((f) => ({ ...f, licensee_email: e.target.value }))} className="h-9 mt-1" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Equipos contratados en el paquete (opcional)</Label>
              <Input
                type="number"
                min={1}
                max={99}
                value={createForm.equipos_contratados}
                onChange={(e) => setCreateForm((f) => ({ ...f, equipos_contratados: e.target.value }))}
                className="h-9 mt-1 font-mono"
                placeholder="Ej. 3 si vendió 3 licencias / 3 PCs"
              />
              <p className="text-[9px] text-slate-500 mt-0.5 leading-snug">
                Mismo comprador puede tener varias licencias (una por ID de instalación). Este dato es la <strong>meta</strong> del contrato; puede subir al emitir con «Equipos del contrato» en el generador.
              </p>
            </div>
            <div>
              <Label className="text-xs">Datos generales (contacto, domicilio, notas)</Label>
              <SpellcheckTextarea
                value={createForm.datos_generales}
                onChange={(e) => setCreateForm((f) => ({ ...f, datos_generales: e.target.value }))}
                rows={3}
                className="w-full mt-1 rounded-sm border border-slate-300 px-2 py-1.5 text-xs"
              />
            </div>
            <PortalAccessFields form={createForm} setForm={setCreateForm} isEdit={false} />
            <BillingBlock form={createForm} setForm={setCreateForm} idPrefix="c" />
            <div>
              <Label className="text-xs">Notas internas</Label>
              <SpellcheckTextarea
                value={createForm.notes}
                onChange={(e) => setCreateForm((f) => ({ ...f, notes: e.target.value }))}
                rows={2}
                className="w-full mt-1 rounded-sm border px-2 py-1 text-xs"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancelar</Button>
            <Button onClick={submitCreate} disabled={creating} className="bg-violet-700">
              {creating && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin inline" />}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto rounded-sm">
          <DialogHeader>
            <DialogTitle>Editar comprador</DialogTitle>
          </DialogHeader>
          {editForm && (
            <div className="space-y-4 text-sm">
              <div className="space-y-3">
                <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-500 m-0">
                  1. Datos generales
                </h4>
              <div>
                <Label className="text-xs">Razón social / nombre</Label>
                <Input value={editForm.licensee_name} onChange={(e) => setEditForm((f) => ({ ...f, licensee_name: e.target.value }))} className="h-9 mt-1" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">RFC</Label>
                  <Input value={editForm.licensee_rfc} onChange={(e) => setEditForm((f) => ({ ...f, licensee_rfc: e.target.value.toUpperCase() }))} className="h-9 mt-1 font-mono" />
                </div>
                <div>
                  <Label className="text-xs">Correo</Label>
                  <Input value={editForm.licensee_email} onChange={(e) => setEditForm((f) => ({ ...f, licensee_email: e.target.value }))} className="h-9 mt-1" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Equipos contratados (meta del paquete)</Label>
                <Input
                  type="number"
                  min={1}
                  max={99}
                  value={editForm.equipos_contratados}
                  onChange={(e) => setEditForm((f) => ({ ...f, equipos_contratados: e.target.value }))}
                  className="h-9 mt-1 font-mono"
                  placeholder="Vacío = no cambiar al guardar (solo si escribes un número)"
                />
              </div>
              <div>
                <Label className="text-xs">Datos generales</Label>
                <SpellcheckTextarea
                  value={editForm.datos_generales}
                  onChange={(e) => setEditForm((f) => ({ ...f, datos_generales: e.target.value }))}
                  rows={3}
                  className="w-full mt-1 rounded-sm border px-2 py-1.5 text-xs"
                />
              </div>
              </div>
              <div className="space-y-3 border-t border-slate-100 pt-3">
                <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-500 m-0">
                  2. Acceso portal comprador
                </h4>
              <PortalAccessFields form={editForm} setForm={setEditForm} isEdit />
              </div>
              <div className="space-y-3 border-t border-slate-100 pt-3">
                <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-500 m-0">
                  3. Facturación y cobro
                </h4>
              <BillingBlock form={editForm} setForm={setEditForm} idPrefix="e" />
              </div>
              <div className="space-y-3 border-t border-slate-100 pt-3">
                <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-500 m-0">
                  4. Notas internas (soporte BNT)
                </h4>
              <div>
                <Label className="text-xs">Notas internas</Label>
                <SpellcheckTextarea
                  value={editForm.notes}
                  onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))}
                  rows={2}
                  className="w-full mt-1 rounded-sm border px-2 py-1 text-xs"
                />
              </div>
              </div>
              <p className="text-[9px] text-slate-500 m-0 leading-snug border-t border-slate-100 pt-2">
                Licencia, módulos y usuarios internos operativos del comprador se gestionan en Cartera,
                Gestor de licencias y módulo Gestión Despacho — no confundir con equipo BNT.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cerrar</Button>
            <Button onClick={submitEdit} disabled={saving} className="bg-violet-700">
              {saving && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin inline" />}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
